import { Button } from '@mui/material';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { JourneyLoadingScreen, StandardJourneyBrandedScreen } from '~/components/StandardJourney';
import JourneySupportDrawer from '~/components/StandardJourney/JourneySupportDrawer';
import { RENEWAL_APP_IDS } from './renewalAppIds';
import RenewalIdentifyStep from './steps/RenewalIdentifyStep';
import RenewalOutcomeStep from './steps/RenewalOutcomeStep';
import RenewalPaymentStep from './steps/RenewalPaymentStep';
import RenewalPhotosStep from './steps/RenewalPhotosStep';
import RenewalReviewStep from './steps/RenewalReviewStep';
import RenewalVerifyStep from './steps/RenewalVerifyStep';
import { getPreviousRenewalStep, getRenewalProgressPercent } from './renewalSteps';
import { useRenewalJourney } from './useRenewalJourney';

/**
 * Renovação de contrato no visual 2026 (Figma «E-commerce — 2026», node 2079:6265).
 * Orquestra `identify → verify → review → payment → processing → outcome`; fora do prazo (`is_renew_outdated`),
 * `processing → photos → outcome`.
 */
export default function RenewalJourneyPage() {
  const { contractId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const {
    step,
    loadState,
    contract,
    channel,
    alternateChannel,
    sendingCode,
    resendingCode,
    code,
    codeStatus,
    quoteState,
    reviewData,
    offerData,
    paymentCards,
    paymentSelection,
    paying,
    publicKey,
    publicKeyLoading,
    outcome,
    failureMessage,
    organizationId,
    photos,
    actions
  } = useRenewalJourney({
    contractHash: contractId,
    organizationId: searchParams.get('org')
  });
  const [supportOpen, setSupportOpen] = useState(false);

  const hasPreviousStep = loadState === 'ready' && getPreviousRenewalStep(step) !== null;

  return (
    <StandardJourneyBrandedScreen
      appId={RENEWAL_APP_IDS.screen}
      contentLayout={
        loadState !== 'ready' || step === 'processing' || (step === 'photos' && photos.loadState !== 'ready')
          ? 'fullscreen'
          : (step === 'review' || step === 'payment' || step === 'outcome') && quoteState === 'ready'
          ? 'checkout'
          : 'card'
      }
      onBack={hasPreviousStep ? actions.goBack : undefined}
      progressPercent={getRenewalProgressPercent(step)}
      showBack={hasPreviousStep}
      showCheckoutStepper={false}
      showHeaderProgressBar={loadState === 'ready'}
    >
      {loadState === 'loading' ? (
        <JourneyLoadingScreen appId={RENEWAL_APP_IDS.loading} message="Carregando seu contrato…" />
      ) : null}

      {loadState === 'error' ? (
        <JourneyLoadingScreen
          action={
            <Button
              data-app-id={RENEWAL_APP_IDS.loadErrorSupport}
              onClick={() => setSupportOpen(true)}
              size="large"
              variant="contained"
            >
              Falar com o atendimento
            </Button>
          }
          appId={RENEWAL_APP_IDS.loadError}
          busy={false}
          description="O link pode estar incompleto ou o contrato não está disponível para renovação. Fale com o atendimento para continuar."
          message="Não encontramos este contrato"
        />
      ) : null}

      {loadState === 'ready' && contract && step === 'identify' ? (
        <RenewalIdentifyStep
          channel={channel}
          contract={contract}
          onChannelChange={actions.setChannel}
          onSend={actions.sendCode}
          onSupport={() => setSupportOpen(true)}
          sending={sendingCode}
        />
      ) : null}

      {loadState === 'ready' && contract && step === 'verify' ? (
        <RenewalVerifyStep
          alternateChannel={alternateChannel}
          channel={channel}
          code={code}
          contract={contract}
          onChangeContact={actions.changeContact}
          onCodeChange={actions.setCode}
          onResend={actions.resendCode}
          onSwitchChannel={actions.switchChannel}
          onVerify={actions.verifyCode}
          resending={resendingCode}
          status={codeStatus}
        />
      ) : null}

      {loadState === 'ready' && step === 'review' && quoteState === 'error' ? (
        <JourneyLoadingScreen
          action={
            <Button
              data-app-id={`${RENEWAL_APP_IDS.quoteError}-retry`}
              onClick={actions.reloadQuote}
              size="large"
              variant="contained"
            >
              Tentar novamente
            </Button>
          }
          appId={RENEWAL_APP_IDS.quoteError}
          busy={false}
          description="Seu código foi verificado, mas não conseguimos carregar os dados da renovação."
          message="Não foi possível carregar sua renovação"
        />
      ) : null}

      {loadState === 'ready' && step === 'review' && quoteState === 'loading' ? (
        <JourneyLoadingScreen appId={RENEWAL_APP_IDS.loading} message="Carregando sua renovação…" />
      ) : null}

      {loadState === 'ready' && step === 'review' && quoteState === 'ready' && reviewData ? (
        <RenewalReviewStep
          data={reviewData}
          offer={
            offerData
              ? {
                  ...offerData,
                  renewing: false,
                  organizationId,
                  onToggleUpsell: actions.toggleUpsell,
                  onRenew: actions.renew
                }
              : undefined
          }
          onSaveAddress={actions.saveAddress}
          onSaveCustomer={actions.saveCustomer}
        />
      ) : null}

      {loadState === 'ready' && step === 'payment' && offerData && reviewData ? (
        <RenewalPaymentStep
          billingAddress={reviewData.address}
          cards={paymentCards}
          onCardAdded={actions.addCard}
          onPay={actions.pay}
          onSelect={actions.selectPayment}
          paying={paying}
          publicKey={publicKey}
          publicKeyLoading={publicKeyLoading}
          selectedId={paymentSelection}
          summary={{
            rows: offerData.summaryRows,
            totalMonthly: offerData.totalMonthly,
            serviceCharge: offerData.serviceCharge
          }}
        />
      ) : null}

      {loadState === 'ready' && step === 'processing' ? (
        <JourneyLoadingScreen appId={RENEWAL_APP_IDS.processing} message="Processando pagamento…" />
      ) : null}

      {loadState === 'ready' && step === 'photos' && photos.loadState === 'loading' ? (
        <JourneyLoadingScreen appId={RENEWAL_APP_IDS.loading} message="Carregando o envio de fotos…" />
      ) : null}

      {loadState === 'ready' && step === 'photos' && photos.loadState === 'error' ? (
        <JourneyLoadingScreen
          action={
            <Button
              data-app-id={`${RENEWAL_APP_IDS.photosLoadError}-retry`}
              onClick={actions.reloadPhotos}
              size="large"
              variant="contained"
            >
              Tentar novamente
            </Button>
          }
          appId={RENEWAL_APP_IDS.photosLoadError}
          busy={false}
          description="Seu pagamento foi aprovado, mas não conseguimos abrir o envio das fotos do equipamento."
          message="Não foi possível carregar o envio de fotos"
        />
      ) : null}

      {loadState === 'ready' && step === 'photos' && photos.loadState === 'ready' ? (
        <RenewalPhotosStep
          errors={photos.errors}
          fields={photos.fields}
          files={photos.files}
          onChangeFile={actions.changePhoto}
          onSubmit={actions.submitPhotos}
          sending={photos.sending}
        />
      ) : null}

      {loadState === 'ready' && step === 'outcome' && outcome ? (
        <RenewalOutcomeStep
          email={contract?.insured?.email ?? ''}
          errorMessage={failureMessage}
          onRetry={actions.pay}
          onUseAnotherCard={actions.useAnotherCard}
          outcome={outcome}
          retrying={paying}
        />
      ) : null}

      <JourneySupportDrawer
        appId={RENEWAL_APP_IDS.supportDrawer}
        onClose={() => setSupportOpen(false)}
        open={supportOpen}
      />
    </StandardJourneyBrandedScreen>
  );
}
